import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { payrollOpsService } from "../../../services/api";

/** Amount display for TDS Eye: no ₹, Indian grouping, whole rupees. */
function money(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

function Line({ label, value, darkMode, indent = 0, bold = false }) {
  return (
    <div
      className={`flex justify-between gap-4 py-1 ${bold ? "font-semibold" : ""}`}
      style={{ paddingLeft: indent ? indent * 12 : undefined }}
    >
      <span className={darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}>{label}</span>
      <span className="shrink-0 tabular-nums">{value}</span>
    </div>
  );
}

function Section({ title, darkMode, children, right = null }) {
  return (
    <section
      className={`rounded-lg border p-3 ${
        darkMode ? "border-[#3C3C3C] bg-[#1f1f1f]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4
          className={`text-[11px] font-semibold uppercase tracking-wide ${
            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          {title}
        </h4>
        {right}
      </div>
      <div className="text-xs">{children}</div>
    </section>
  );
}

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/**
 * Eye modal: full STI-style TDS computation with component monthly/annual,
 * exemptions, regime slabs (Statutory Rates as of pay month), PAN, month annexure.
 * One Time TDS detail: use Onetime Tax on the TDS Comparison toolbar (not duplicated here).
 */
export default function TdsComputationBreakdownModal({
  row,
  payMonth,
  darkMode = false,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!row?.employeeId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const liabilityRes = await payrollOpsService.getTaxLiability(row.employeeId, payMonth);
        if (cancelled) return;
        setData(liabilityRes?.data || liabilityRes);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || "Unable to load TDS computation");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row?.employeeId, payMonth]);

  const ill = data?.illustration || {};
  const cj = data?.computationJson || {};
  const breakdown = ill.deductionBreakdown || cj.deductionBreakdown || {};
  const taxDetail = ill.taxDetail || cj.taxDetail || {};
  const ratesMeta = ill.taxRatesMeta || cj.taxRatesMeta || {};
  const history = data?.monthlyHistory || [];

  const componentPack = ill.componentBreakdown?.components?.length
    ? ill.componentBreakdown
    : cj.componentBreakdown?.components?.length
      ? cj.componentBreakdown
      : { components: [] };

  const components = componentPack.components || [];
  const annexure = ill.salaryMonthAnnexure || cj.salaryMonthAnnexure || null;
  const annexureRows = Array.isArray(annexure?.rows) ? annexure.rows : [];

  const taxableComponents = annexureRows.length
    ? annexureRows
        .filter(
          (r) =>
            (r.section === "Earnings" || r.section === "Reimbursements") &&
            String(r.taxStatus || "").toLowerCase() !== "exempt"
        )
        .map((r) => ({
          name: r.name,
          annual: Number(r.total) || 0,
          isOneTimeTax: !!r.isOneTimeTax,
        }))
    : components
        .filter(
          (c) =>
            (c.isTaxable || c.taxStatus === "Taxable") &&
            (c.section === "Earnings" || c.section === "Reimbursements" || !c.section)
        )
        .map((c) => ({
          name: c.name,
          annual: Number(c.annual) || 0,
        }));
  const exemptComponents = components.filter(
    (c) => c.taxStatus === "Exempt" || c.taxStatus === "Partial"
  );
  const taxableGrossAnnual = round2(
    taxableComponents.reduce((s, c) => s + (Number(c.annual) || 0), 0) ||
      Number(annexure?.summaryTotal) ||
      Number(ill.annualGross) ||
      0
  );

  // Tax gross = this-employer annexure (paid/projected) + previous employer income (Tax Planning)
  const prevEmployerGross = round2(
    Number(ill.prevEmployerGross ?? cj.prevEmployerGross ?? annexure?.prevEmployerGross) || 0
  );
  const prevEmployerNameRaw = String(
    ill.prevEmployerName ?? cj.prevEmployerName ?? annexure?.prevEmployerName ?? ""
  ).trim();
  const prevEmployerName =
    prevEmployerNameRaw && !/^not\s*applicable$/i.test(prevEmployerNameRaw)
      ? prevEmployerNameRaw
      : null;
  const prevEmployerIncomeLabel = prevEmployerName
    ? `Previous Employer Income - ${prevEmployerName}`
    : "Previous Employer Income";

  const displayGross = round2(
    Number(annexure?.taxAnnualGross) > 0
      ? Number(annexure.taxAnnualGross)
      : Number(annexure?.summaryTotal) > 0
        ? round2(Number(annexure.summaryTotal) + prevEmployerGross)
        : Number(ill.annualGross) > 0
          ? Number(ill.annualGross)
          : taxableGrossAnnual
  );
  const section80Ccd2Amount = round2(
    Number(
      breakdown.section80CCD2 ??
        cj.employerNpsCcd2Annual ??
        ill.employerNpsCcd2Annual
    ) || 0
  );
  const displayDeductions = round2(
    Number(ill.totalDeductions ?? cj.deductions) ||
      Number(breakdown.standardDeduction || 0) +
        (String(ill.regime || cj.regime || "").toUpperCase() === "OLD"
          ? Number(breakdown.professionalTax || 0)
          : 0) +
        Number(breakdown.hraExemption || 0) +
        Number(breakdown.section80C || 0) +
        section80Ccd2Amount +
        Number(breakdown.flexiExemption || 0) +
        Number(breakdown.viaDeductions || 0) +
        (breakdown.housePropertyNetIncome != null
          ? -(Number(breakdown.housePropertyNetIncome) || 0)
          : Number(breakdown.housePropertyInterest || 0))
  );
  // Prefer backend taxable income (regime-correct); fall back to gross − deductions
  const displayTaxableIncome = round2(
    Number(ill.annualTaxableIncome ?? cj.annualTaxableIncome) > 0
      ? Number(ill.annualTaxableIncome ?? cj.annualTaxableIncome)
      : Math.max(0, displayGross - displayDeductions)
  );

  if (!row || typeof document === "undefined") return null;

  const border = darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const pan = ill.panNumber || data?.panNumber || cj.panNumber || "—";
  const regime = ill.regime || row.taxRegime || "—";
  const structureLabel =
    ill.structureName || cj.structureName || "—";

  const annualTaxLiability = Number(ill.annualTaxLiability) || 0;
  const tdsYtdBeforeMonth = Number(ill.tdsDeductedYtd) || 0;
  const prevEmployerTax = Number(ill.prevEmployerTax ?? cj.prevEmployerTax) || 0;
  const remainingMonths = Number(ill.remainingMonths) || 1;
  const taxStillBeforeMonth = Math.max(
    0,
    Math.round(annualTaxLiability - tdsYtdBeforeMonth - prevEmployerTax)
  );
  const computedMonthTds = Number(ill.computedTds ?? row.computedTds) || 0;
  const appliedMonthTds = Number(ill.currentMonthTds ?? row.actualTds) || 0;
  const taxStillAfterMonth =
    ill.projectedBalanceTax != null
      ? Number(ill.projectedBalanceTax)
      : Math.max(0, taxStillBeforeMonth - appliedMonthTds);

  const isPaidMonthKind = (kind) =>
    kind === "actual" ||
    kind === "current_paid" ||
    kind === "current_paid_arrears" ||
    kind === "actual_structure" ||
    kind === "lop_reversal" ||
    kind === "one_time_tax" ||
    kind === "paid" ||
    kind === "projected_to_date";

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-start justify-center bg-black/50 p-4 pt-12 pb-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[calc(100vh-4rem)] w-full max-w-6xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tds-computation-title"
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${border}`}>
          <div>
            <h3 id="tds-computation-title" className="text-sm font-semibold">
              {row.empCode || ill.empCode || "—"} — {row.name || ill.employeeName || "Employee"} · TDS
              computation
            </h3>
            <p className={`text-xs ${muted}`}>
              FY {ill.financialYear || cj.financialYear || "—"} · {regime} regime ·{" "}
              {payMonth || ill.payMonth || "—"} · PAN:{" "}
              <span className="font-medium tabular-nums">{pan}</span>
              {" · "}
              Structure: {structureLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`rounded p-1.5 ${darkMode ? "hover:bg-[hsl(var(--ds-muted))]" : "hover:bg-[hsl(var(--ds-muted))]"}`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {loading ? (
            <div className={`flex items-center justify-center gap-2 py-12 text-sm ${muted}`}>
              <Loader2 size={18} className="animate-spin" /> Loading computation…
            </div>
          ) : error ? (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-4 text-sm text-amber-900">
              {error}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                {[
                  ["PAN", pan],
                  ["Tax regime", regime],
                  ["Computed (month)", money(ill.computedTds ?? row.computedTds)],
                  ["Applied", money(ill.currentMonthTds ?? row.actualTds)],
                  ["Annual tax", money(ill.annualTaxLiability)],
                  ["Remaining months", ill.remainingMonths ?? "—"],
                ].map(([label, val]) => (
                  <div key={label} className={`rounded border p-2 text-xs ${border}`}>
                    <p className={muted}>{label}</p>
                    <p className="font-semibold tabular-nums">{val}</p>
                  </div>
                ))}
              </div>

              {annexure?.rows?.length ? (
                <Section title="1. Income from salaries — month annexure (FY)" darkMode={darkMode}>
                  <div className={`overflow-auto rounded border ${border}`}>
                    <table className="w-full min-w-[960px] border-separate border-spacing-0 text-[10px]">
                      <thead className={darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"}>
                        <tr>
                          <th
                            className={`sticky left-0 z-30 min-w-[148px] max-w-[190px] px-1.5 py-1 text-left font-medium shadow-[2px_0_4px_-2px_rgba(0,0,0,0.2)] ${
                              darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"
                            }`}
                          >
                            Particulars
                          </th>
                          {(annexure.months || []).map((mk, idx) => {
                            const kind =
                              annexure.monthMeta?.[mk]?.kind || annexure.sourceByMonth?.[mk];
                            const isPaid = isPaidMonthKind(kind);
                            const lab = (annexure.monthLabels || [])[idx] || mk;
                            return (
                              <th
                                key={mk}
                                className={`px-1.5 py-1 text-right font-medium tabular-nums ${
                                  isPaid
                                    ? darkMode
                                      ? "text-emerald-400"
                                      : "text-emerald-700"
                                    : muted
                                }`}
                                title={
                                  kind === "actual"
                                    ? "Paid — processed payroll (recurring)"
                                    : kind === "current_paid" ||
                                        kind === "current_paid_arrears"
                                      ? "Current payroll month — counted when running Declarations & TDS (structure + arrears in this month)"
                                      : kind === "not_processed"
                                        ? "Not processed — excluded from paid income until payroll is processed for this month"
                                        : kind === "paid_structure"
                                          ? "Legacy — structure assumed paid"
                                          : "Projected — assigned structure"
                                }
                              >
                                {lab}
                                {isPaid ? "*" : kind === "not_processed" ? "†" : ""}
                              </th>
                            );
                          })}
                          <th className="px-1.5 py-1 text-right font-medium">Paid till</th>
                          <th className="px-1.5 py-1 text-right font-medium">Projected</th>
                          <th className="px-1.5 py-1 text-right font-medium">FY total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const bySec = new Map();
                          annexure.rows.forEach((r) => {
                            const sec = r.section || "Earnings";
                            if (!bySec.has(sec)) bySec.set(sec, []);
                            bySec.get(sec).push(r);
                          });
                          const sectionOrder = [
                            "Earnings",
                            "Reimbursements",
                            "Employer contributions",
                            "Deductions",
                          ];
                          const ordered = [
                            ...sectionOrder.filter((s) => bySec.has(s)),
                            ...[...bySec.keys()].filter((s) => !sectionOrder.includes(s)),
                          ];
                          const stickyCell = darkMode ? "bg-[#1f1f1f]" : "bg-[hsl(var(--ds-surface))]";
                          const stickySec = darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]";
                          const stickyFoot = darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]";
                          return ordered
                            .map((sec) => (
                              <React.Fragment key={sec}>
                                <tr
                                  className={
                                    darkMode ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]" : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                                  }
                                >
                                  <td
                                    className={`sticky left-0 z-20 px-1.5 py-1 font-semibold shadow-[2px_0_4px_-2px_rgba(0,0,0,0.2)] ${stickySec}`}
                                  >
                                    {sec}
                                  </td>
                                  <td colSpan={(annexure.months || []).length + 3} />
                                </tr>
                                {bySec.get(sec).map((r) => (
                                  <tr
                                    key={`${sec}-${r.name}`}
                                    className={
                                      r.isOneTimeTax
                                        ? darkMode
                                          ? "border-t border-amber-900/40 bg-amber-950/20"
                                          : "border-t border-amber-100 bg-amber-50/60"
                                        : darkMode
                                          ? "border-t border-[#333]"
                                          : "border-t border-[hsl(var(--ds-border))]"
                                    }
                                  >
                                    <td
                                      className={`sticky left-0 z-20 min-w-[148px] max-w-[190px] px-1.5 py-1 pl-3 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.2)] ${
                                        r.isOneTimeTax
                                          ? darkMode
                                            ? "bg-amber-950/40"
                                            : "bg-amber-50"
                                          : stickyCell
                                      }`}
                                    >
                                      <span
                                        className="block truncate"
                                        title={`${r.name} (${r.taxStatus || "—"})`}
                                      >
                                        {r.name}
                                        <span className={`ml-1 ${muted}`}>
                                          ({r.taxStatus || "—"})
                                        </span>
                                      </span>
                                    </td>
                                    {(annexure.months || []).map((mk) => {
                                      const src = r.sourceByMonth?.[mk];
                                      const isPaid = isPaidMonthKind(src);
                                      return (
                                        <td
                                          key={mk}
                                          className={`px-1.5 py-1 text-right tabular-nums ${
                                            isPaid ? "font-semibold" : ""
                                          }`}
                                        >
                                          {money(r.byMonth?.[mk])}
                                        </td>
                                      );
                                    })}
                                    <td className="px-1.5 py-1 text-right font-medium tabular-nums">
                                      {money(r.paidTillCurrent)}
                                    </td>
                                    <td className="px-1.5 py-1 text-right tabular-nums">
                                      {money(r.projectedRest)}
                                    </td>
                                    <td className="px-1.5 py-1 text-right font-medium tabular-nums">
                                      {money(r.total)}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))
                            .concat([
                              <tr
                                key="gross-salary-row"
                                className={
                                  darkMode
                                    ? "border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                                    : "border-t bg-[hsl(var(--ds-muted))]"
                                }
                              >
                                <td
                                  className={`sticky left-0 z-20 px-1.5 py-1.5 font-semibold shadow-[2px_0_4px_-2px_rgba(0,0,0,0.2)] ${stickyFoot}`}
                                >
                                  Gross salary (taxable earnings)
                                </td>
                                {(annexure.months || []).map((mk) => (
                                  <td
                                    key={mk}
                                    className="px-1.5 py-1.5 text-right font-semibold tabular-nums"
                                  >
                                    {money(annexure.summaryByMonth?.[mk])}
                                  </td>
                                ))}
                                <td className="px-1.5 py-1.5 text-right font-semibold tabular-nums">
                                  {money(annexure.paidTillCurrent)}
                                </td>
                                <td className="px-1.5 py-1.5 text-right font-semibold tabular-nums">
                                  {money(annexure.projectedRest)}
                                </td>
                                <td className="px-1.5 py-1.5 text-right font-semibold tabular-nums">
                                  {money(annexure.summaryTotal)}
                                </td>
                              </tr>,
                            ]);
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <p className={`mt-1.5 text-[10px] leading-snug ${muted}`}>
                    * Paid / current Declarations &amp; TDS month. † Not processed — ₹0 in the grid
                    (excluded from paid income). Previous employer income / TDS from Tax Planning
                    (Previous Employment) are applied in the annual tax computation below. Revision
                    arrears are added component-wise in the current pay month only. Fixed
                    reimbursements (e.g. Telephone, Fuel) do not scale with CTC. One Time tax
                    Monthly Variables (highlighted) are included in Gross salary in the month entered.
                    For One Time TDS breakup (regular vs One Time, surcharge), use{" "}
                    <span className="font-medium">Onetime Tax</span> on the TDS Comparison toolbar.
                  </p>
                </Section>
              ) : (
                <Section title="1. Income from salaries — month annexure (FY)" darkMode={darkMode}>
                  <p className={muted}>
                    No assigned structure components found for the month annexure. Open Assigned
                    Structure for this employee and Save, then reopen Eye.
                  </p>
                </Section>
              )}

              <Section title="2. Income from salaries · taxable & exemptions" darkMode={darkMode}>
                <div className="mb-2 grid gap-2 sm:grid-cols-2">
                  <div className={`rounded border p-2 ${border}`}>
                    <p className={`mb-1 text-[10px] font-semibold uppercase ${muted}`}>
                      Taxable earnings (annualised)
                    </p>
                    {taxableComponents.length ? (
                      taxableComponents.map((c, i) => (
                        <Line
                          key={`t-${c.name}-${i}`}
                          label={c.name}
                          value={money(c.annual)}
                          darkMode={darkMode}
                        />
                      ))
                    ) : (
                      <>
                        <Line
                          label="Basic salary (annualised)"
                          value={money(ill.basicAnnual)}
                          darkMode={darkMode}
                        />
                        <Line
                          label="House rent allowance"
                          value={money(ill.hraAnnual)}
                          darkMode={darkMode}
                        />
                        <Line
                          label="Other allowances"
                          value={money(ill.otherAllowancesAnnual)}
                          darkMode={darkMode}
                        />
                      </>
                    )}
                    <Line
                      label="Annexure gross (paid + current + future)"
                      value={money(annexure?.summaryTotal || displayGross)}
                      darkMode={darkMode}
                    />
                    {prevEmployerGross > 0 ? (
                      <Line
                        label={prevEmployerIncomeLabel}
                        value={money(prevEmployerGross)}
                        darkMode={darkMode}
                      />
                    ) : null}
                    <Line
                      label="Total gross salary"
                      value={money(displayGross)}
                      darkMode={darkMode}
                      bold
                    />
                  </div>
                  <div className={`rounded border p-2 ${border}`}>
                    <p className={`mb-1 text-[10px] font-semibold uppercase ${muted}`}>
                      Exemptions & deductions
                    </p>
                    {exemptComponents.length > 0 &&
                      exemptComponents.map((c, i) => (
                        <Line
                          key={`e-${c.name}-${i}`}
                          label={`${c.name} (exempt)`}
                          value={money(c.annual)}
                          darkMode={darkMode}
                        />
                      ))}
                    <Line
                      label="Less: Standard deduction U/s 16(ia)"
                      value={money(breakdown.standardDeduction)}
                      darkMode={darkMode}
                    />
                    {String(regime).toUpperCase() === "OLD" &&
                      Number(breakdown.professionalTax) > 0 && (
                        <Line
                          label="Less: Professional Tax U/s 16(iii)"
                          value={money(breakdown.professionalTax)}
                          darkMode={darkMode}
                        />
                      )}
                    {String(regime).toUpperCase() === "NEW" && (
                      <p className={`mb-1 text-[10px] italic ${muted}`}>
                        Professional Tax U/s 16(iii) is not allowed under the NEW regime.
                      </p>
                    )}
                    {Number(breakdown.hraExemption) > 0 && (
                      <Line
                        label="Less: HRA exemption"
                        value={money(breakdown.hraExemption)}
                        darkMode={darkMode}
                      />
                    )}
                    {Number(breakdown.section80C) > 0 && (
                      <Line
                        label={
                          Number(breakdown.section80CFromPayroll) > 0
                            ? `Less: Section 80C (incl. Emp. PF ${money(breakdown.section80CFromPayroll)})`
                            : "Less: Section 80C"
                        }
                        value={money(breakdown.section80C)}
                        darkMode={darkMode}
                      />
                    )}
                    {Number(breakdown.section80C) <= 0 &&
                      Number(breakdown.section80CFromPayroll) > 0 && (
                        <Line
                          label="Less: Employee PF under 80C (payroll)"
                          value={money(breakdown.section80CFromPayroll)}
                          darkMode={darkMode}
                        />
                      )}
                    {section80Ccd2Amount > 0 && (
                      <Line
                        label="Less: Employer NPS U/s 80CCD(2)"
                        value={money(section80Ccd2Amount)}
                        darkMode={darkMode}
                      />
                    )}
                    {Number(breakdown.flexiExemption) > 0 && (
                      <Line
                        label="Less: Flexi / FBP exemption"
                        value={money(breakdown.flexiExemption)}
                        darkMode={darkMode}
                      />
                    )}
                    {Number(breakdown.viaDeductions) > 0 && (
                      <Line
                        label="Less: Chapter VI-A"
                        value={money(breakdown.viaDeductions)}
                        darkMode={darkMode}
                      />
                    )}
                    {breakdown.housePropertyNetIncome != null &&
                      Number(breakdown.housePropertyNetIncome) !== 0 && (
                      <Line
                        label={
                          Number(breakdown.housePropertyNetIncome) < 0
                            ? "Less: House property loss"
                            : "Add: House property income"
                        }
                        value={money(Math.abs(breakdown.housePropertyNetIncome))}
                        darkMode={darkMode}
                      />
                    )}
                    {Number(breakdown.housePropertyInterest) > 0 &&
                      breakdown.housePropertyNetIncome == null && (
                      <Line
                        label="Less: House property interest"
                        value={money(breakdown.housePropertyInterest)}
                        darkMode={darkMode}
                      />
                    )}
                    <Line
                      label="Total deductions"
                      value={money(displayDeductions)}
                      darkMode={darkMode}
                    />
                    <Line
                      label="Gross total income / Taxable income"
                      value={money(displayTaxableIncome)}
                      darkMode={darkMode}
                      bold
                    />
                  </div>
                </div>
                <p className={`mt-1 text-[10px] ${muted}`}>
                  Tax regime: <strong>{regime}</strong> · Declaration: {ill.declarationStatus || "—"}
                  {breakdown.regimeNote ? ` · ${breakdown.regimeNote}` : ""}
                  {" · "}Exempt components annual: {money(componentPack.exemptAnnual)}
                </p>
              </Section>

              <Section
                title={`3. Tax regime slabs (${regime}) — Statutory Rates`}
                darkMode={darkMode}
                right={
                  <span className={`text-[10px] ${muted}`}>
                    As of {ratesMeta.ratesEffectiveDate || "—"}
                    {ratesMeta.historyMatchedDate
                      ? ` · rates from ${ratesMeta.historyMatchedDate}`
                      : ""}
                    {ratesMeta.ratesSource ? ` · ${ratesMeta.ratesSource}` : ""}
                    {ratesMeta.cess != null ? ` · cess ${ratesMeta.cess}%` : ""}
                    {ratesMeta.standardDeduction != null
                      ? ` · std ded. ${money(ratesMeta.standardDeduction)}`
                      : ""}
                  </span>
                }
              >
                {(taxDetail.slabRows || []).length ? (
                  <div className={`mb-2 overflow-auto rounded border ${border}`}>
                    <table className="w-full min-w-[520px] text-[11px]">
                      <thead className={darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"}>
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium">Slab</th>
                          <th className="px-2 py-1.5 text-right font-medium">Rate</th>
                          <th className="px-2 py-1.5 text-right font-medium">Income in slab</th>
                          <th className="px-2 py-1.5 text-right font-medium">Slab tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxDetail.slabRows.map((s) => (
                          <tr
                            key={s.slNo || s.label}
                            className={
                              darkMode ? "border-t border-[#333]" : "border-t border-[hsl(var(--ds-border))]"
                            }
                          >
                            <td className="px-2 py-1">{s.label}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{s.rate}%</td>
                            <td className="px-2 py-1 text-right tabular-nums">
                              {money(s.incomeInSlab)}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums">{money(s.slabTax)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : ratesMeta.slabs?.length ? (
                  <div className={`mb-2 overflow-auto rounded border ${border}`}>
                    <table className="w-full min-w-[400px] text-[11px]">
                      <thead className={darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"}>
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium">Slab range</th>
                          <th className="px-2 py-1.5 text-right font-medium">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ratesMeta.slabs.map((s, i) => (
                          <tr
                            key={i}
                            className={
                              darkMode ? "border-t border-[#333]" : "border-t border-[hsl(var(--ds-border))]"
                            }
                          >
                            <td className="px-2 py-1">
                              {(Number(s.min) || 0).toLocaleString("en-IN")}
                              {" – "}
                              {s.max == null
                                ? "and above"
                                : Number(s.max).toLocaleString("en-IN")}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums">{s.rate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className={`mb-2 ${muted}`}>No slab configuration found for this regime.</p>
                )}
                <Line
                  label="Tax before surcharge, rebate & cess"
                  value={money(taxDetail.baseTax)}
                  darkMode={darkMode}
                />
                {Number(taxDetail.rebate87A) > 0 ? (
                  <>
                    <Line
                      label="Less: Rebate U/s 87A"
                      value={money(taxDetail.rebate87A)}
                      darkMode={darkMode}
                    />
                    <Line
                      label="Tax after rebate"
                      value={money(taxDetail.taxAfterRebate ?? taxDetail.baseTax - taxDetail.rebate87A)}
                      darkMode={darkMode}
                    />
                  </>
                ) : (
                  <p className={`mb-1 text-[10px] ${muted}`}>
                    {taxDetail.rebateDetail?.note ||
                      (String(regime).toUpperCase() === "NEW"
                        ? "Rebate U/s 87A: up to ₹60,000 when taxable income ≤ ₹12,00,000 (not applicable at this income)."
                        : "Rebate U/s 87A: up to ₹12,500 when taxable income ≤ ₹5,00,000 (not applicable at this income).")}
                  </p>
                )}
                <Line label="Surcharge" value={money(taxDetail.surcharge)} darkMode={darkMode} />
                <Line
                  label={`Health & education cess (${taxDetail.cessRate ?? ratesMeta.cess ?? 4}%)`}
                  value={money(taxDetail.cess)}
                  darkMode={darkMode}
                />
                <Line
                  label="Annual tax liability"
                  value={money(ill.annualTaxLiability)}
                  darkMode={darkMode}
                  bold
                />
              </Section>

              <Section title="4. Prepaid taxes & month TDS" darkMode={darkMode}>
                <Line
                  label="Annual tax liability"
                  value={money(annualTaxLiability)}
                  darkMode={darkMode}
                />
                <Line
                  label="Less: TDS already deducted this FY (prior months)"
                  value={money(tdsYtdBeforeMonth)}
                  darkMode={darkMode}
                  indent={1}
                />
                <Line
                  label="Less: Previous employer TDS (declaration)"
                  value={money(prevEmployerTax)}
                  darkMode={darkMode}
                  indent={1}
                />
                <Line
                  label="Tax still to recover (before this month)"
                  value={money(taxStillBeforeMonth)}
                  darkMode={darkMode}
                  bold
                />
                <Line
                  label="Remaining months in FY (incl. this month)"
                  value={String(remainingMonths)}
                  darkMode={darkMode}
                />
                <Line
                  label="TDS for this month (still to recover ÷ remaining months)"
                  value={money(computedMonthTds)}
                  darkMode={darkMode}
                  bold
                />
                <Line
                  label={
                    ill.isOverridden || row.isOverridden
                      ? "TDS applied this month (manual override)"
                      : "TDS applied this month"
                  }
                  value={money(appliedMonthTds)}
                  darkMode={darkMode}
                  bold
                />
                <Line
                  label="Tax still to recover after this month"
                  value={money(taxStillAfterMonth)}
                  darkMode={darkMode}
                />

                {(() => {
                  const cmp = ill.regimeComparison || cj.regimeComparison || null;
                  if (!cmp?.old || !cmp?.new) return null;
                  const oldBetter = cmp.recommendedRegime === "OLD";
                  const newBetter = cmp.recommendedRegime === "NEW";
                  const selected = String(cmp.selectedRegime || regime || "").toUpperCase();
                  const badgeBase =
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold";
                  return (
                    <div
                      className={`mt-3 space-y-2 rounded border p-2.5 ${border} ${
                        darkMode ? "bg-[#1f1f1f]" : "bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold">Regime comparison (same salary)</p>
                        <span
                          className={`${badgeBase} ${
                            cmp.selectedIsOptimal
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-amber-100 text-amber-900"
                          }`}
                        >
                          {cmp.selectedIsOptimal
                            ? `Selected ${selected} is optimal`
                            : `Consider switching to ${cmp.recommendedRegime}`}
                        </span>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div
                          className={`rounded border p-2 ${border} ${
                            oldBetter ? (darkMode ? "bg-emerald-950/40" : "bg-emerald-50") : ""
                          }`}
                        >
                          <div className="mb-1 flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] font-semibold">OLD regime</span>
                            {selected === "OLD" && (
                              <span className={`${badgeBase} bg-blue-100 text-blue-800`}>
                                Selected
                              </span>
                            )}
                            {oldBetter && (
                              <span className={`${badgeBase} bg-emerald-600 text-white`}>
                                Recommended
                              </span>
                            )}
                          </div>
                          <Line
                            label="Taxable income"
                            value={money(cmp.old.annualTaxableIncome)}
                            darkMode={darkMode}
                          />
                          <Line
                            label="Total deductions"
                            value={money(cmp.old.totalDeductions)}
                            darkMode={darkMode}
                          />
                          <Line
                            label="Annual tax liability"
                            value={money(cmp.old.annualTaxLiability)}
                            darkMode={darkMode}
                            bold
                          />
                          <p className={`mt-1 text-[10px] ${muted}`}>
                            Std. ded. {money(cmp.old.standardDeduction || 0)}
                            {Number(cmp.old.professionalTax) > 0
                              ? ` · PT ${money(cmp.old.professionalTax)}`
                              : ""}
                            {Number(cmp.old.section80CFromPayroll) > 0
                              ? ` · Emp. PF in 80C ${money(cmp.old.section80CFromPayroll)}`
                              : ""}
                            {Number(cmp.old.section80CCD2) > 0
                              ? ` · Emp. NPS 80CCD(2) ${money(cmp.old.section80CCD2)}`
                              : ""}
                            {Number(cmp.old.rebate87A) > 0
                              ? ` · 87A rebate ${money(cmp.old.rebate87A)}`
                              : ""}
                          </p>
                        </div>
                        <div
                          className={`rounded border p-2 ${border} ${
                            newBetter ? (darkMode ? "bg-emerald-950/40" : "bg-emerald-50") : ""
                          }`}
                        >
                          <div className="mb-1 flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] font-semibold">NEW regime</span>
                            {selected === "NEW" && (
                              <span className={`${badgeBase} bg-blue-100 text-blue-800`}>
                                Selected
                              </span>
                            )}
                            {newBetter && (
                              <span className={`${badgeBase} bg-emerald-600 text-white`}>
                                Recommended
                              </span>
                            )}
                          </div>
                          <Line
                            label="Taxable income"
                            value={money(cmp.new.annualTaxableIncome)}
                            darkMode={darkMode}
                          />
                          <Line
                            label="Total deductions"
                            value={money(cmp.new.totalDeductions)}
                            darkMode={darkMode}
                          />
                          <Line
                            label="Annual tax liability"
                            value={money(cmp.new.annualTaxLiability)}
                            darkMode={darkMode}
                            bold
                          />
                          <p className={`mt-1 text-[10px] ${muted}`}>
                            Std. ded. {money(cmp.new.standardDeduction || 0)}
                            {Number(cmp.new.section80CCD2) > 0
                              ? ` · Emp. NPS 80CCD(2) ${money(cmp.new.section80CCD2)}`
                              : ""}
                            {" — "}PT / emp 80C / HRA not allowed
                            {Number(cmp.new.rebate87A) > 0
                              ? ` · 87A rebate ${money(cmp.new.rebate87A)}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <p className={`text-[10px] ${muted}`}>{cmp.recommendation}</p>
                    </div>
                  );
                })()}
              </Section>

              {history.length > 0 && (
                <Section title="5. Monthly TDS history (this FY)" darkMode={darkMode}>
                  <div className={`overflow-auto rounded border ${border}`}>
                    <table className="w-full min-w-[400px] text-[11px]">
                      <thead className={darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"}>
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium">Month</th>
                          <th className="px-2 py-1.5 text-right font-medium">Computed</th>
                          <th className="px-2 py-1.5 text-right font-medium">Applied</th>
                          <th className="px-2 py-1.5 text-left font-medium">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h) => (
                          <tr
                            key={h.payMonth}
                            className={
                              darkMode ? "border-t border-[#333]" : "border-t border-[hsl(var(--ds-border))]"
                            }
                          >
                            <td className="px-2 py-1 tabular-nums">{h.payMonth}</td>
                            <td className="px-2 py-1 text-right tabular-nums">
                              {money(h.computedTds)}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums">
                              {money(h.actualTds)}
                            </td>
                            <td className={`px-2 py-1 ${muted}`}>
                              {h.isOverridden ? "Override" : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
